import {useEffect, useLayoutEffect, useMemo, useRef} from 'react'
import type {ClientTool, ClientToolCall} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {ToolDefinition} from '@microsoft/agent-host-protocol'
import {registerTool, unregisterTool} from './registry'

type ToolHandler = (
  params: Record<string, unknown>,
  call: ClientToolCall,
) => ReturnType<NonNullable<ClientTool['fulfil']>>

/**
 * Registers a client tool for the lifetime of the component. The framework
 * handles stale-closure prevention — the handler always sees current state.
 *
 * @param definition - Static tool metadata (name, description, schema). Should
 *   be a module-level constant so the reference never changes. Pass `null` to
 *   skip registration (e.g. when behind a feature flag).
 * @param handler - Called when the agent invokes this tool. Receives parsed
 *   params and the underlying call metadata. May close over component state
 *   freely — the framework ensures the latest handler is always invoked.
 */
export function useClientTool(definition: ToolDefinition | null, handler: ToolHandler): void {
  const handlerRef = useRef(handler)
  useLayoutEffect(() => {
    handlerRef.current = handler
  })

  const tool = useMemo((): ClientTool | null => {
    if (!definition) return null
    return {
      definition,
      fulfil(call: ClientToolCall) {
        return handlerRef.current(JSON.parse(call.toolInput ?? '{}') as Record<string, unknown>, call)
      },
    }
  }, [definition])

  useEffect(() => {
    if (!tool) return
    registerTool(tool)
    return () => unregisterTool(tool.definition.name, tool)
  }, [tool])
}
